import { useCallback, useEffect, useState } from 'react'
import { eur } from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { PorteGerant } from '../composants/PorteGerant'
import { useReveil } from '../composants/Volets'
import { useToast } from '../etat/Toast'

/**
 * La caisse de la journée.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Le geste du soir
 * ────────────────────────────────────────────────────────────────────────────
 *
 * On ferme, on compte, on range. L'écran suit cet ordre : les tickets du jour
 * tels qu'ils sont passés, le total encaissé par mode de paiement, puis un
 * bouton qui arrête le compte. Rien ne se verrouille — une vente oubliée
 * s'encaisse encore à 19 h 05 — la clôture est un constat, consigné au journal
 * avec le nom de celui qui l'a fait.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Ce que la clôture révèle, et à qui
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Le magasin vend au prix de l'étiquette, et l'étiquette ne bouge pas quand le
 * contrat prévoit une baisse. Un DVD affiché 10 € en rapporte 10 alors que son
 * contrat, au bout de deux mois, ne prévoyait plus que 8. Personne ne voit
 * jamais cet écart : il est diffus, quelques euros à la fois.
 *
 * Il est donc totalisé au moment où l'on compte la caisse — mais **derrière le
 * code gérant**. Ce n'est pas un chiffre de comptoir : c'est une question de
 * gestion, qui touche à ce que la boutique retient et à ce qu'elle reverse.
 */

interface Ticket {
  id: number
  ticketNo: number
  creeLe: string
  totalCentimes: number
  mode: string
  vendeurNom: string
  nbArticles: number
}

interface LigneTicket {
  articleId: number | null
  ref: string
  libelle: string
  categorie: string
  quantite: number
  prixCentimes: number
  prixReferenceCentimes: number
  deposantId: number | null
  deposantNom: string | null
}

type ColonnePiece =
  | 'libelle'
  | 'deposantNom'
  | 'moisEnRayon'
  | 'encaisseCentimes'
  | 'prixContratCentimes'
  | 'ecart'

interface TicketDetail extends Ticket {
  lignes: LigneTicket[]
  paiements: { mode: string; montantCentimes: number }[]
  reverseCentimes: number
  rendueCentimes: number
}

interface Piece {
  ligneId: number
  venteId: number
  ref: string
  libelle: string
  deposantNom: string | null
  ticketNo: number
  mode: string
  moisEnRayon: number
  encaisseCentimes: number
  prixContratCentimes: number
  ecartCentimes: number
  corrigeable: boolean
}

interface Journee {
  jour: string
  tickets: Ticket[]
  totalCentimes: number
  nbTickets: number
  nbArticles: number
  parMode: { mode: string; totalCentimes: number }[]
  demarqueOubliee: {
    ecartCentimes: number
    nbPieces: number
    parMode: { mode: string; ecartCentimes: number; nbPieces: number }[]
    pieces: Piece[]
  }
}

const HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' })
const JOUR = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const LIBELLE_MODE: Record<string, string> = {
  especes: 'Espèces',
  cb: 'Carte',
  mixte: 'Mixte',
}

/** La date du jour au format de l'API, en heure locale et non UTC. */
function aujourdhui(decalage = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + decalage)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`
}

export function Journee() {
  const { signaler, alerter } = useToast()
  const [jour, setJour] = useState(() => aujourdhui())
  const [donnees, setDonnees] = useState<Journee | null>(null)
  const [cloture, setCloture] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  /** Le ticket dont on veut se défaire, en attente de confirmation. */
  const [aAnnuler, setAAnnuler] = useState<Ticket | null>(null)
  /** Le ticket ouvert au clic sur son numéro — son détail se charge à part,
   *  la liste du jour ne porte que le résumé (articles, total). */
  const [ticketOuvertId, setTicketOuvertId] = useState<number | null>(null)
  const [ticketDetail, setTicketDetail] = useState<TicketDetail | null>(null)
  const [chargementTicket, setChargementTicket] = useState(false)

  useEffect(() => {
    if (ticketOuvertId == null) {
      setTicketDetail(null)
      return
    }
    setChargementTicket(true)
    setTicketDetail(null)
    api<{ vente: TicketDetail }>(`/ventes/${ticketOuvertId}`)
      .then((r) => setTicketDetail(r.vente))
      .catch((cause) => alerter(cause instanceof ErreurApi ? cause.message : 'Ticket introuvable.'))
      .finally(() => setChargementTicket(false))
  }, [ticketOuvertId, alerter])

  const charger = useCallback(() => {
    api<Journee>(`/journee?date=${jour}`)
      .then(setDonnees)
      .catch((cause) => alerter(cause instanceof ErreurApi ? cause.message : 'Chargement impossible.'))
  }, [jour, alerter])

  useEffect(charger, [charger])
  useReveil('/journee', charger)

  const valider = async () => {
    if (!donnees || envoi) return
    setEnvoi(true)
    try {
      await api('/journee/cloture', {
        methode: 'POST',
        corps: {
          jour: donnees.jour,
          totalCentimes: donnees.totalCentimes,
          nbTickets: donnees.nbTickets,
          ecartCentimes: donnees.demarqueOubliee.ecartCentimes,
        },
      })
      signaler(`Caisse du ${jour} clôturée.`)
      setCloture(false)
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Clôture impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  /**
   * Défaire un ticket.
   *
   * On demande confirmation en montrant le montant : l'erreur d'annulation
   * coûte plus cher que l'erreur de vente, puisqu'elle remet en rayon des
   * pièces que le client emporte.
   */
  const annuler = async (ticket: Ticket, motif: string) => {
    if (envoi) return
    setEnvoi(true)
    try {
      const r = await api<{ ticketNo: number; articlesRendus: number }>(
        `/ventes/${ticket.id}/annuler`,
        { methode: 'POST', corps: { motif } },
      )
      signaler(
        `Ticket ${r.ticketNo} annulé · ${r.articlesRendus} article${r.articlesRendus > 1 ? 's' : ''} remis en rayon.`,
      )
      setAAnnuler(null)
      charger()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Annulation impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  const outils = (
    <div className="seg" style={{ marginLeft: 'auto' }}>
      <button
        type="button"
        className="seg-opt"
        aria-pressed={jour === aujourdhui()}
        onClick={() => setJour(aujourdhui())}
      >
        Aujourd’hui
      </button>
      <button
        type="button"
        className="seg-opt"
        aria-pressed={jour === aujourdhui(-1)}
        onClick={() => setJour(aujourdhui(-1))}
      >
        Hier
      </button>
    </div>
  )

  const titreJour = JOUR.format(new Date(`${jour}T12:00:00`))

  return (
    <Ecran titre="Journée" sousTitre={titreJour} outils={outils}>
      {aAnnuler && (
        <ConfirmationAnnulation
          ticket={aAnnuler}
          envoi={envoi}
          onFermer={() => setAAnnuler(null)}
          onConfirmer={(motif) => void annuler(aAnnuler, motif)}
        />
      )}

      {ticketOuvertId != null && (
        <DetailTicket
          chargement={chargementTicket}
          ticket={ticketDetail}
          onFermer={() => setTicketOuvertId(null)}
          onAnnuler={() => {
            const ticket = donnees?.tickets.find((t) => t.id === ticketOuvertId)
            if (ticket) {
              setTicketOuvertId(null)
              setAAnnuler(ticket)
            }
          }}
        />
      )}

      {cloture && donnees && (
        <ResumeCloture
          journee={donnees}
          envoi={envoi}
          onFermer={() => setCloture(false)}
          onValider={() => void valider()}
          onCorrige={charger}
        />
      )}

      <div className="bandeau-journee">
        <div className="chiffre-journee">
          <span className="etiquette">Encaissé</span>
          <strong>{eur(donnees?.totalCentimes ?? 0)}</strong>
        </div>
        <div className="chiffre-journee">
          <span className="etiquette">Tickets</span>
          <strong>{donnees?.nbTickets ?? 0}</strong>
        </div>
        <div className="chiffre-journee">
          <span className="etiquette">Articles</span>
          <strong>{donnees?.nbArticles ?? 0}</strong>
        </div>
        {(donnees?.parMode ?? []).map((m) => (
          <div className="chiffre-journee" key={m.mode}>
            <span className="etiquette">{LIBELLE_MODE[m.mode] ?? m.mode}</span>
            <strong>{eur(m.totalCentimes)}</strong>
          </div>
        ))}
      </div>

      <div className="zone-defilante">
        {donnees && donnees.tickets.length === 0 && (
          <div className="vide">Aucune vente ce jour-là.</div>
        )}

        {donnees && donnees.tickets.length > 0 && (
          <table className="tableau">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Heure</th>
                <th>Vendeur</th>
                <th>Articles</th>
                <th>Règlement</th>
                <th className="a-droite">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {donnees.tickets.map((t) => (
                <tr key={t.id}>
                  <td className="ref">
                    <button
                      type="button"
                      className="btn btn-fantome"
                      style={{ padding: 0 }}
                      onClick={() => setTicketOuvertId(t.id)}
                    >
                      n° {t.ticketNo}
                    </button>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{HEURE.format(new Date(t.creeLe))}</td>
                  <td className="titre">{t.vendeurNom}</td>
                  <td className="sourdine">{t.nbArticles}</td>
                  <td className="sourdine">{LIBELLE_MODE[t.mode] ?? t.mode}</td>
                  <td className="a-droite chiffre">{eur(t.totalCentimes)}</td>
                  <td className="a-droite">
                    <button
                      type="button"
                      className="btn btn-fantome"
                      onClick={() => setAAnnuler(t)}
                    >
                      Annuler
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="pied-encaissement">
        <div className="panneau-pied" style={{ borderTop: 0 }}>
          <button
            type="button"
            className="btn btn-primaire btn-large"
            style={{ flex: 1 }}
            disabled={!donnees}
            onClick={() => setCloture(true)}
          >
            Clôturer la journée — {eur(donnees?.totalCentimes ?? 0)}
          </button>
        </div>
      </div>
    </Ecran>
  )
}

/**
 * Le récapitulatif de clôture.
 *
 * Les chiffres de caisse sont visibles de tous ; l'écart de démarque attend le
 * code gérant. Il n'est pas caché par pudeur : c'est un chiffre qui se lit avec
 * son explication, et qui ne veut rien dire pour qui n'a pas le contrat en tête.
 */
function ResumeCloture({
  journee,
  envoi,
  onFermer,
  onValider,
  onCorrige,
}: {
  journee: Journee
  envoi: boolean
  onFermer: () => void
  onValider: () => void
  onCorrige: () => void
}) {
  const d = journee.demarqueOubliee
  const [aCorriger, setACorriger] = useState<Piece | null>(null)

  // La liste est déjà entièrement chargée — quelques dizaines de pièces au
  // plus par jour — donc le tri se fait ici, sans redemander au serveur.
  const [triPieces, setTriPieces] = useState<{ colonne: ColonnePiece; sens: 'asc' | 'desc' }>({
    colonne: 'ecart',
    sens: 'desc',
  })
  const changerTriPieces = (colonne: ColonnePiece) => {
    setTriPieces((actuel) =>
      actuel.colonne === colonne
        ? { colonne, sens: actuel.sens === 'asc' ? 'desc' : 'asc' }
        : { colonne, sens: 'desc' },
    )
  }
  const piecesTriees = [...d.pieces].sort((a, b) => {
    const val = (p: Piece): string | number => {
      switch (triPieces.colonne) {
        case 'libelle':
          return p.libelle
        case 'deposantNom':
          return p.deposantNom ?? ''
        case 'moisEnRayon':
          return p.moisEnRayon
        case 'encaisseCentimes':
          return p.encaisseCentimes
        case 'prixContratCentimes':
          return p.prixContratCentimes
        case 'ecart':
          return p.ecartCentimes
      }
    }
    const [va, vb] = [val(a), val(b)]
    const comparaison = typeof va === 'string' ? va.localeCompare(vb as string) : va - (vb as number)
    return triPieces.sens === 'asc' ? comparaison : -comparaison
  })

  return (
    <div className="voile" onClick={onFermer}>
      <div className="panneau-cloture panneau-plein-ecran" onClick={(e) => e.stopPropagation()}>
        <div className="panneau-entete">
          <h4 style={{ margin: 0 }}>Clôture de la caisse</h4>
          <button type="button" className="btn btn-fantome" onClick={onFermer}>
            Fermer
          </button>
        </div>

        <div className="cloture-chiffres">
          <div>
            <span className="etiquette">Encaissé</span>
            <div className="chiffre" style={{ fontSize: 30 }}>
              {eur(journee.totalCentimes)}
            </div>
          </div>
          <div>
            <span className="etiquette">Tickets</span>
            <div className="chiffre" style={{ fontSize: 30 }}>
              {journee.nbTickets}
            </div>
          </div>
          {journee.parMode.map((m) => (
            <div key={m.mode}>
              <span className="etiquette">{LIBELLE_MODE[m.mode] ?? m.mode}</span>
              <div className="chiffre" style={{ fontSize: 30 }}>
                {eur(m.totalCentimes)}
              </div>
            </div>
          ))}
        </div>

        <PorteGerant compact titre="La démarque non appliquée">
          {() => (
            <div className="bloc-fiche">
              <h3 className="bloc-fiche-titre">
                {eur(d.ecartCentimes)} encaissés au-dessus du prix dû aujourd’hui (après démarque)
              </h3>

              {d.nbPieces === 0 ? (
                <p className="sourdine" style={{ fontSize: 13 }}>
                  Aucune pièce vendue aujourd’hui n’avait droit à une baisse. L’étiquette et le
                  contrat disaient la même chose.
                </p>
              ) : (
                <>
                  <p style={{ fontSize: 13, marginTop: 0 }}>
                    <strong>{d.nbPieces}</strong> pièce{d.nbPieces > 1 ? 's' : ''} vendue
                    {d.nbPieces > 1 ? 's' : ''} au-dessus du prix prévu au contrat — l’étiquette
                    n’avait pas été refaite, et le client a payé le prix affiché.
                  </p>

                  <p className="sourdine" style={{ fontSize: 12 }}>
                    Cette somme revient <strong>entièrement à la boutique</strong> : le déposant
                    est payé sur le prix prévu à son contrat, celui qu’il a signé, et non sur ce
                    que le client a donné. C’est donc ce que rapporte l’étiquette qu’on n’a pas
                    eu le temps de recoller.
                  </p>

                  {/* La même somme, selon la façon dont elle est entrée en
                      caisse : c'est ce qu'on retrouve dans le tiroir le soir. */}
                  <div className="cloture-partage">
                    {d.parMode.map((m) => (
                      <div key={m.mode}>
                        <span className="etiquette">{LIBELLE_MODE[m.mode] ?? m.mode}</span>
                        <div className="chiffre">{eur(m.ecartCentimes)}</div>
                        <span className="sourdine">
                          {m.nbPieces} pièce{m.nbPieces > 1 ? 's' : ''}
                        </span>
                      </div>
                    ))}
                  </div>

                  <table className="tableau">
                    <thead>
                      <tr>
                        <th>N°</th>
                        <th>
                          <EnteteTri colonne="libelle" tri={triPieces} onTri={changerTriPieces}>
                            Désignation
                          </EnteteTri>
                        </th>
                        <th>
                          <EnteteTri colonne="deposantNom" tri={triPieces} onTri={changerTriPieces}>
                            Client
                          </EnteteTri>
                        </th>
                        <th>
                          <EnteteTri colonne="moisEnRayon" tri={triPieces} onTri={changerTriPieces}>
                            En rayon
                          </EnteteTri>
                        </th>
                        <th>Ticket</th>
                        <th>Réglé en</th>
                        <th className="a-droite">
                          <EnteteTri colonne="encaisseCentimes" tri={triPieces} onTri={changerTriPieces}>
                            Encaissé
                          </EnteteTri>
                        </th>
                        <th
                          className="a-droite"
                          title="Le prix initial du dépôt, réduit par la démarque automatique prévue au contrat pour l'ancienneté de la pièce."
                        >
                          <EnteteTri colonne="prixContratCentimes" tri={triPieces} onTri={changerTriPieces}>
                            Dû aujourd’hui (démarque)
                          </EnteteTri>
                        </th>
                        <th className="a-droite">
                          <EnteteTri colonne="ecart" tri={triPieces} onTri={changerTriPieces}>
                            Écart
                          </EnteteTri>
                        </th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {piecesTriees.map((p) => (
                        <tr key={`${p.ticketNo}-${p.ref}`}>
                          <td className="ref">{p.ref}</td>
                          <td className="titre">{p.libelle}</td>
                          <td className="sourdine">{p.deposantNom ?? '—'}</td>
                          <td className="sourdine" style={{ whiteSpace: 'nowrap' }}>
                            {p.moisEnRayon} mois
                          </td>
                          <td className="sourdine">n° {p.ticketNo}</td>
                          <td className="sourdine">{LIBELLE_MODE[p.mode] ?? p.mode}</td>
                          <td className="a-droite chiffre">{eur(p.encaisseCentimes)}</td>
                          <td className="a-droite chiffre sourdine">
                            {eur(p.prixContratCentimes)}
                          </td>
                          <td className="a-droite chiffre">
                            <strong>{eur(p.ecartCentimes)}</strong>
                          </td>
                          <td className="a-droite">
                            {p.corrigeable ? (
                              <button
                                type="button"
                                className="btn btn-fantome"
                                onClick={() => setACorriger(p)}
                              >
                                Revoir
                              </button>
                            ) : (
                              <span className="sourdine" style={{ fontSize: 11 }} title="Déjà reversé au déposant">
                                reversé
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </PorteGerant>

        <div className="panneau-pied">
          <button type="button" className="btn btn-secondaire btn-large" onClick={onFermer}>
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-primaire btn-large"
            style={{ flex: 1 }}
            disabled={envoi}
            onClick={onValider}
          >
            Valider la caisse du jour
          </button>
        </div>
      </div>

      {aCorriger && (
        <CorrectionPiece
          piece={aCorriger}
          onFermer={() => setACorriger(null)}
          onCorrige={() => {
            setACorriger(null)
            onCorrige()
          }}
        />
      )}
    </div>
  )
}

/**
 * Corriger le prix encaissé d'une pièce, sans annuler tout le ticket.
 *
 * Pour une erreur de caisse — « le ticket dit 6 €, on a en réalité reçu 4 € »
 * — pas pour revenir sur un geste commercial déjà fait au comptoir.
 */
function CorrectionPiece({
  piece,
  onFermer,
  onCorrige,
}: {
  piece: Piece
  onFermer: () => void
  onCorrige: () => void
}) {
  const { alerter, signaler } = useToast()
  const [saisie, setSaisie] = useState(String(piece.encaisseCentimes / 100).replace('.', ','))
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const nouveauCentimes = Math.round(parseFloat(saisie.replace(',', '.')) * 100)
  const valide = Number.isFinite(nouveauCentimes) && nouveauCentimes > 0

  const valider = async () => {
    if (!valide || envoi) return
    setEnvoi(true)
    try {
      await api(`/ventes/${piece.venteId}/lignes/${piece.ligneId}/corriger`, {
        methode: 'POST',
        corps: { prixCentimes: nouveauCentimes, motif },
      })
      signaler(`${piece.ref} corrigé à ${eur(nouveauCentimes)}.`)
      onCorrige()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Correction impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <div className="voile voile-centre" onMouseDown={onFermer} onClick={onFermer}>
      <div
        className="panneau-cloture"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="panneau-entete">
          <h4 style={{ margin: 0 }}>Corriger {piece.ref}</h4>
          <button type="button" className="btn btn-fantome" onClick={onFermer}>
            Fermer
          </button>
        </div>

        <div style={{ padding: '14px 20px 4px' }}>
          <div className="titre" style={{ fontSize: 15 }}>
            {piece.libelle}
          </div>
          <div className="sourdine" style={{ fontSize: 12 }}>
            Ticket n° {piece.ticketNo} · encaissé à {eur(piece.encaisseCentimes)} · dû au contrat{' '}
            {eur(piece.prixContratCentimes)}
          </div>
        </div>

        <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="champ">
            <label htmlFor="correction-montant">Montant réellement encaissé</label>
            <input
              id="correction-montant"
              className="saisie saisie-grande"
              inputMode="decimal"
              value={saisie}
              autoFocus
              onChange={(e) => setSaisie(e.target.value)}
            />
          </div>
          <div className="champ">
            <label htmlFor="correction-motif">Pourquoi ? (facultatif)</label>
            <input
              id="correction-motif"
              className="saisie"
              placeholder="erreur de saisie au pavé, rendu de monnaie…"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </div>
          <div className="note">
            La part du déposant se recalcule sur ce nouveau montant, plafonnée par le prix
            affiché au moment de la vente — jamais plus que ce qui a été réellement encaissé.
          </div>
        </div>

        <div className="panneau-pied">
          <button type="button" className="btn btn-secondaire btn-large" onClick={onFermer}>
            Annuler
          </button>
          <button
            type="button"
            className="btn btn-primaire btn-large"
            style={{ flex: 1 }}
            disabled={!valide || envoi}
            onClick={() => void valider()}
          >
            Enregistrer la correction
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * La confirmation d'annulation.
 *
 * Elle montre ce qu'on s'apprête à défaire — le numéro, l'heure, le montant,
 * qui l'a encaissé — parce qu'un ticket s'annule sur un mot du client et qu'on
 * se trompe de ligne vite. Le motif est facultatif mais il est demandé : c'est
 * lui qu'on relira dans six mois en cherchant pourquoi la caisse ne tombait pas.
 */
function ConfirmationAnnulation({
  ticket,
  envoi,
  onFermer,
  onConfirmer,
}: {
  ticket: Ticket
  envoi: boolean
  onFermer: () => void
  onConfirmer: (motif: string) => void
}) {
  const [motif, setMotif] = useState('')

  return (
    <div className="voile voile-centre" onMouseDown={onFermer}>
      <div className="panneau-cloture" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="panneau-entete">
          <h4 style={{ margin: 0 }}>Annuler le ticket n° {ticket.ticketNo} ?</h4>
          <button type="button" className="btn btn-fantome" onClick={onFermer}>
            Fermer
          </button>
        </div>

        <div className="cloture-chiffres">
          <div>
            <span className="etiquette">Montant</span>
            <div className="chiffre" style={{ fontSize: 30 }}>{eur(ticket.totalCentimes)}</div>
          </div>
          <div>
            <span className="etiquette">Articles</span>
            <div className="chiffre" style={{ fontSize: 30 }}>{ticket.nbArticles}</div>
          </div>
          <div>
            <span className="etiquette">Encaissé à</span>
            <div className="chiffre" style={{ fontSize: 30 }}>{HEURE.format(new Date(ticket.creeLe))}</div>
          </div>
          <div>
            <span className="etiquette">Par</span>
            <div className="chiffre" style={{ fontSize: 30 }}>{ticket.vendeurNom}</div>
          </div>
        </div>

        <div className="note" style={{ margin: '14px 18px' }}>
          Les articles retournent en rayon et le ticket disparaît des comptes. Le journal en
          garde le détail complet — rien n'est perdu, mais rien ne se remet en place tout seul :
          il faudra ré-encaisser si c'était une erreur.
        </div>

        <div style={{ padding: '0 18px 14px' }}>
          <label htmlFor="motif-annulation" className="etiquette">
            Pourquoi ? (facultatif)
          </label>
          <input
            id="motif-annulation"
            className="saisie"
            style={{ width: '100%', marginTop: 6 }}
            placeholder="erreur de saisie, le client s’est ravisé…"
            value={motif}
            autoFocus
            onChange={(e) => setMotif(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onConfirmer(motif)
            }}
          />
        </div>

        <div className="panneau-pied">
          <button type="button" className="btn btn-secondaire btn-large" onClick={onFermer}>
            Garder le ticket
          </button>
          <button
            type="button"
            className="btn btn-primaire btn-large"
            style={{ flex: 1 }}
            disabled={envoi}
            onClick={() => onConfirmer(motif)}
          >
            Annuler le ticket — {eur(ticket.totalCentimes)}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Le détail d'un ticket, ouvert d'un clic sur son numéro dans la liste du
 * jour.
 *
 * Jusqu'ici la ligne de la journée ne disait que « 2 articles » : pour savoir
 * lesquels, il fallait aller les rechercher un par un dans le rayon. Le
 * ticket sait déjà tout — articles, déposants, prix, mode de règlement — il
 * suffit de le rouvrir.
 */
function DetailTicket({
  ticket,
  chargement,
  onFermer,
  onAnnuler,
}: {
  ticket: TicketDetail | null
  chargement: boolean
  onFermer: () => void
  onAnnuler: () => void
}) {
  return (
    <div className="voile voile-centre" onMouseDown={onFermer}>
      <div
        className="panneau-cloture"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="panneau-entete">
          <h4 style={{ margin: 0 }}>
            {ticket ? `Ticket n° ${ticket.ticketNo}` : 'Ticket'}
          </h4>
          <button type="button" className="btn btn-fantome" onClick={onFermer}>
            Fermer
          </button>
        </div>

        {chargement && <div className="vide">Chargement…</div>}

        {ticket && (
          <>
            <div className="cloture-chiffres">
              <div>
                <span className="etiquette">Total</span>
                <div className="chiffre" style={{ fontSize: 30 }}>{eur(ticket.totalCentimes)}</div>
              </div>
              <div>
                <span className="etiquette">Encaissé à</span>
                <div className="chiffre" style={{ fontSize: 30 }}>
                  {HEURE.format(new Date(ticket.creeLe))}
                </div>
              </div>
              <div>
                <span className="etiquette">Vendeur</span>
                <div className="chiffre" style={{ fontSize: 30 }}>{ticket.vendeurNom}</div>
              </div>
              <div>
                <span className="etiquette">Réglé en</span>
                <div className="chiffre" style={{ fontSize: 30 }}>
                  {LIBELLE_MODE[ticket.mode] ?? ticket.mode}
                </div>
              </div>
            </div>

            <div className="zone-defilante" style={{ padding: '0 18px' }}>
              <table className="tableau">
                <thead>
                  <tr>
                    <th>Réf.</th>
                    <th>Désignation</th>
                    <th>Déposant</th>
                    <th className="a-droite">Qté</th>
                    <th className="a-droite">Prix</th>
                    <th className="a-droite">Étiquette</th>
                  </tr>
                </thead>
                <tbody>
                  {ticket.lignes.map((l, i) => (
                    <tr key={`${l.ref}-${i}`}>
                      <td className="ref">{l.ref}</td>
                      <td className="titre">{l.libelle}</td>
                      <td className="sourdine">{l.deposantNom ?? '—'}</td>
                      <td className="a-droite">{l.quantite}</td>
                      <td className="a-droite chiffre">{eur(l.prixCentimes * l.quantite)}</td>
                      <td className="a-droite chiffre sourdine">
                        {l.prixCentimes !== l.prixReferenceCentimes
                          ? eur(l.prixReferenceCentimes * l.quantite)
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="panneau-pied">
          <button type="button" className="btn btn-secondaire btn-large" onClick={onFermer}>
            Fermer
          </button>
          {ticket && (
            <button type="button" className="btn btn-fantome" onClick={onAnnuler}>
              Annuler ce ticket
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/** Un en-tête de colonne triable, pour une liste déjà entièrement en main —
 *  le tri se fait dans le navigateur, pas par un aller-retour au serveur. */
function EnteteTri({
  colonne,
  tri,
  onTri,
  children,
}: {
  colonne: ColonnePiece
  tri: { colonne: ColonnePiece; sens: 'asc' | 'desc' }
  onTri: (c: ColonnePiece) => void
  children: React.ReactNode
}) {
  const actif = tri.colonne === colonne
  return (
    <button
      type="button"
      className={actif ? 'tri-colonne actif' : 'tri-colonne'}
      aria-pressed={actif}
      onClick={() => onTri(colonne)}
      title={
        actif ? `Trier en ordre ${tri.sens === 'asc' ? 'décroissant' : 'croissant'}` : 'Trier sur cette colonne'
      }
    >
      {children}
      <span className="tri-fleche" aria-hidden="true">
        {actif ? (tri.sens === 'asc' ? '↑' : '↓') : '⇅'}
      </span>
    </button>
  )
}
